import { useCallback, useEffect, useState } from "react";

/*
 * API client for the Shooting Results backend (ShootingApp-Backend).
 *
 * The portal holds no built-in data: every figure on every page comes from the
 * API, which is populated by importing a shoot workbook on Admin → Import Data.
 * Until a shoot is imported the endpoints return empty sets and the pages render
 * their empty states.
 */

/*
 * The API lives on the same machine as the frontend, port 5001 — so the API
 * host is derived from whatever host the browser loaded the page from. That
 * makes the same build work on localhost AND on the server's public IP/domain
 * without configuration: a visitor at http://135.181.168.128:3001 calls
 * http://135.181.168.128:5001, a local dev at localhost:3001 calls
 * localhost:5001. A hardcoded "localhost" here would make every remote
 * visitor's browser call itself instead of the server.
 *
 * REACT_APP_API_URL remains as an override for when the API is hosted
 * somewhere else entirely (e.g. behind a reverse proxy on another domain).
 */
export const API_BASE =
  process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:5001/api`;

type ApiEnvelope<T> = { success: boolean; data: T; message?: string };

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem("srp_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/*
 * A 401 means the token is gone or expired. Clearing it here keeps the UI
 * honest: the next admin route guard sends the user to the login page instead of
 * showing a signed-in shell whose every request fails.
 */
const handleUnauthorized = (status: number) => {
  if (status === 401) adminSession.clear();
};

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeader() });
  if (!res.ok) {
    handleUnauthorized(res.status);
    throw new Error(`GET ${path} → ${res.status}`);
  }
  const body: ApiEnvelope<T> = await res.json();
  if (!body.success) throw new Error(body.message || `GET ${path} failed`);
  return body.data;
}

async function send<T>(method: string, path: string, payload?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
  const body = await res.json();
  if (!res.ok || !body.success) {
    handleUnauthorized(res.status);
    throw new Error(body.message || `${method} ${path} failed`);
  }
  return body as T;
}

export const apiPost = <T,>(path: string, payload: unknown) => send<T>("POST", path, payload);
export const apiPut = <T,>(path: string, payload: unknown) => send<T>("PUT", path, payload);
export const apiDelete = <T,>(path: string) => send<T>("DELETE", path);

/** Multipart upload (used by the shotslog import). */
export async function apiUpload<T>(path: string, file: File, fields: Record<string, string> = {}): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers: authHeader(), body: form });
  const body = await res.json();
  if (!res.ok || !body.success) {
    handleUnauthorized(res.status);
    throw new Error(body.message || `Upload to ${path} failed`);
  }
  return body as T;
}

/**
 * Fetch `path` and expose it with loading/error state.
 *
 * `empty` is what `data` holds before the response arrives and if the request
 * fails — `[]` for collections, `null` for a single record. Pass `null` as the
 * path to skip the request entirely (e.g. a route parameter is missing).
 * `reload()` refetches.
 */
export function useApi<T>(
  path: string | null,
  empty: T
): { data: T; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<T>(empty);
  const [loading, setLoading] = useState<boolean>(!!path);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) {
      setData(empty);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGet<T>(path)
      .then((res) => {
        if (cancelled) return;
        setData(res === null || res === undefined ? empty : res);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setData(empty);
        setError(e.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `empty` is a literal at every call site; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, reload };
}

/* ------------------------------ Admin session ------------------------------ */
/*
 * The session is a JWT in localStorage, so it survives page reloads and trips
 * out to the public site — the administrator stays signed in until the token
 * expires or they log out. `valid()` also checks the token's own expiry, so a
 * stale token does not let someone into an admin screen that then 401s on every
 * request.
 */

const TOKEN_KEY = "srp_admin_token";
const USER_KEY = `${TOKEN_KEY}_user`;

export interface AdminUser {
  username: string;
  email?: string;
  name?: string;
  role?: string;
}

/** Reads `exp` out of a JWT payload without verifying it (the API does that). */
const tokenExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const adminSession = {
  save(token: string, user: AdminUser) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  user(): AdminUser | null {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  },
  /** A token that exists and has not expired. */
  valid(): boolean {
    const token = this.token();
    if (!token) return false;
    const exp = tokenExpiry(token);
    if (exp !== null && exp <= Date.now()) {
      this.clear();
      return false;
    }
    return true;
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

export async function adminLogin(username: string, password: string) {
  const body = await apiPost<{ success: boolean; token: string; user: AdminUser }>("/auth/admin/login", {
    username,
    password,
  });
  adminSession.save(body.token, body.user);
  return body;
}
